import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class Percent {
  public static get Prototype(): Percent {
    return new Percent(0);
  }

  public static readonly fromJSON = (value: number | null | undefined): Percent =>
    new Percent(value ?? 0);

  public static readonly fromUnknownJSON = (
    json: JSONType
  ): Percent | null | undefined => {
    if (isNullOrUndefined(json)) return json;
    else if (isValidPercentJSON(json, { throw: true })) {
      return Percent.fromJSON(json);
    }
    return undefined;
  };

  public static validate(value: number): void {
    if (Number.isNaN(value)) throw new Error("Percent value must be a number");
    if (value < 0 || value > 1)
      throw new Error("Percent value must be between 0 and 1");
  }

  public get isEmpty(): boolean {
    return this.value === 0;
  }

  public constructor(public readonly value: number) {
    Percent.validate(value);
  }

  public toJSON(): number {
    return this.value;
  }

  public valueOf(): number {
    return this.value;
  }

  public toString(): string {
    return formatPercent(this.value);
  }

  public isEqualTo(other?: unknown): boolean {
    if (!(other instanceof Percent)) return false;
    return this.value === other?.value;
  }

  public clone = (): this => this; // Immutable
}

function isValidPercentJSON(
  json: unknown,
  options?: { throw: boolean }
): json is number | null | undefined {
  const opts = options ?? { throw: false };
  if (isNullOrUndefined(json) || typeof json === "number") return true;
  if (opts.throw)
    throw new Error("Cannot create Percent from non-number value");
  return false;
}

/**
 * Converts the stored fractional value to the percentage value users expect to see.
 *
 * Percent values are persisted as fractions (`0.055`), while every user-facing percentage needs
 * percentage points (`5.50%`). This is intentionally a pure conversion: the `Percent` constructor
 * validates persisted/domain values, while redline controls may need to display transient invalid
 * values long enough to clamp them on dismissal.
 */
export function percentageValue(value: number): number {
  return value * 100;
}

export function formatPercent(value: number, options?: { trimTrailingZeros?: boolean }): string {
  return formatPercentagePoints(percentageValue(value), options);
}

export function formatPercentagePoints(value: number, options?: { trimTrailingZeros?: boolean }): string {
  if (!Number.isFinite(value)) {
    return `${value}%`;
  }

  const formattedValue = value.toFixed(2);
  const displayValue = options?.trimTrailingZeros ? Number(formattedValue).toString() : formattedValue;
  return `${displayValue}%`;
}
